import Decimal from 'decimal.js';
import { supabase } from '../lib/supabase';

class DeFiService {
  constructor() {
    this.stakingPools = [];
    this.yieldFarms = [];
    this.liquidityPools = [];
  }

  // Save DeFi position to database
  async saveDeFiPosition(userId, walletId, positionData) {
    try {
      const { data, error } = await supabase?.from('defi_positions')?.insert({
          user_id: userId,
          wallet_id: walletId,
          position_type: positionData?.type,
          protocol: positionData?.protocol,
          chain: positionData?.chain,
          asset_symbol: positionData?.asset,
          staked_amount: positionData?.stakedAmount,
          current_value: positionData?.currentValue,
          apy: positionData?.apy,
          earned_amount: positionData?.earnedAmount || 0,
          earned_value: positionData?.earnedValue || 0,
          status: positionData?.status || 'active',
          start_date: positionData?.startDate || new Date()?.toISOString()
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving DeFi position:', error);
      throw error;
    }
  }

  // Get user's DeFi positions from database
  async getUserDeFiPositions(userId) {
    try {
      const { data, error } = await supabase?.from('defi_positions')?.select('*')?.eq('user_id', userId)?.eq('status', 'active')?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching DeFi positions:', error);
      return [];
    }
  }

  // Update DeFi position
  async updateDeFiPosition(positionId, updates) {
    try {
      const { data, error } = await supabase?.from('defi_positions')?.update({
          ...updates,
          last_updated: new Date()?.toISOString()
        })?.eq('id', positionId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating DeFi position:', error);
      throw error;
    }
  }

  // Get available staking opportunities
  async getStakingOpportunities(chain = 'all') {
    // Real staking opportunities - these are reference data, not user-specific
    const opportunities = [
      {
        id: 'eth-staking',
        protocol: 'Ethereum 2.0',
        asset: 'ETH',
        apy: 4.2,
        tvl: 28500000000,
        minStake: 0.01,
        lockPeriod: 'Flexible',
        risk: 'Low',
        chain: 'ethereum',
        icon: 'Hexagon',
        color: '#627EEA'
      },
      {
        id: 'sol-staking',
        protocol: 'Solana Validators',
        asset: 'SOL',
        apy: 6.8,
        tvl: 12300000000,
        minStake: 0.01,
        lockPeriod: '2-3 days',
        risk: 'Low',
        chain: 'solana',
        icon: 'Zap',
        color: '#14F195'
      },
      {
        id: 'usdc-lending',
        protocol: 'Aave',
        asset: 'USDC',
        apy: 3.5,
        tvl: 5600000000,
        minStake: 10,
        lockPeriod: 'None',
        risk: 'Low',
        chain: 'ethereum',
        icon: 'DollarSign',
        color: '#2775CA'
      }
    ];

    if (chain === 'all') {
      return opportunities;
    }

    return opportunities?.filter(opp => opp?.chain === chain);
  }

  // Get user's active staking positions
  async getStakingPositions(walletAddress) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return [];

      const positions = await this.getUserDeFiPositions(user?.id);
      return positions?.filter(p => p?.position_type === 'staking')?.map(p => ({
        id: p?.id,
        protocol: p?.protocol,
        asset: p?.asset_symbol,
        stakedAmount: parseFloat(p?.staked_amount),
        currentValue: parseFloat(p?.current_value),
        apy: parseFloat(p?.apy),
        earned: parseFloat(p?.earned_amount),
        earnedValue: parseFloat(p?.earned_value),
        startDate: p?.start_date,
        status: p?.status,
        chain: p?.chain
      }));
    } catch (error) {
      console.error('Error fetching staking positions:', error);
      return [];
    }
  }

  // Calculate staking rewards
  calculateStakingRewards(amount, apy, days) {
    const principal = new Decimal(amount);
    const rate = new Decimal(apy)?.div(100)?.div(365);
    const periods = new Decimal(days);

    const rewards = principal?.mul(rate)?.mul(periods);
    return rewards?.toNumber();
  }

  // Get yield farming opportunities
  async getYieldFarmingOpportunities(chain = 'all') {
    const farms = [
      {
        id: 'uni-eth-usdc',
        protocol: 'Uniswap V3',
        pair: 'ETH/USDC',
        apy: 12.5,
        tvl: 850000000,
        rewards: ['UNI'],
        risk: 'Medium',
        chain: 'ethereum',
        impermanentLoss: 'Medium'
      },
      {
        id: 'ray-sol-usdc',
        protocol: 'Raydium',
        pair: 'SOL/USDC',
        apy: 18.3,
        tvl: 320000000,
        rewards: ['RAY'],
        risk: 'Medium',
        chain: 'solana',
        impermanentLoss: 'Medium'
      },
      {
        id: 'curve-3pool',
        protocol: 'Curve',
        pair: 'DAI/USDC/USDT',
        apy: 8.2,
        tvl: 1200000000,
        rewards: ['CRV'],
        risk: 'Low',
        chain: 'ethereum',
        impermanentLoss: 'Low'
      }
    ];

    if (chain === 'all') {
      return farms;
    }

    return farms?.filter(farm => farm?.chain === chain);
  }

  // Get user's yield farming positions
  async getYieldFarmingPositions(walletAddress) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return [];

      const positions = await this.getUserDeFiPositions(user?.id);
      return positions?.filter(p => p?.position_type === 'yield_farming')?.map(p => ({
        id: p?.id,
        protocol: p?.protocol,
        pair: p?.asset_symbol,
        liquidityProvided: parseFloat(p?.staked_amount),
        currentValue: parseFloat(p?.current_value),
        apy: parseFloat(p?.apy),
        earned: parseFloat(p?.earned_value),
        rewards: [],
        startDate: p?.start_date,
        status: p?.status,
        chain: p?.chain
      }));
    } catch (error) {
      console.error('Error fetching yield farming positions:', error);
      return [];
    }
  }

  // Get liquidity pool information
  async getLiquidityPools(chain = 'all') {
    const pools = [
      {
        id: 'pool-1',
        protocol: 'Uniswap V3',
        pair: 'ETH/USDC',
        tvl: 850000000,
        volume24h: 125000000,
        fees24h: 375000,
        apy: 12.5,
        chain: 'ethereum'
      },
      {
        id: 'pool-2',
        protocol: 'Raydium',
        pair: 'SOL/USDC',
        tvl: 320000000,
        volume24h: 58000000,
        fees24h: 174000,
        apy: 18.3,
        chain: 'solana'
      }
    ];

    if (chain === 'all') {
      return pools;
    }

    return pools?.filter(pool => pool?.chain === chain);
  }

  // Calculate impermanent loss
  calculateImpermanentLoss(priceRatio) {
    // Formula: IL = 2 * sqrt(priceRatio) / (1 + priceRatio) - 1
    const ratio = new Decimal(priceRatio);
    const numerator = new Decimal(2)?.mul(ratio?.sqrt());
    const denominator = new Decimal(1)?.add(ratio);
    const il = numerator?.div(denominator)?.sub(1)?.mul(100);
    
    return il?.toNumber();
  }

  // Get DeFi portfolio summary
  async getDeFiPortfolioSummary(walletAddress) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return {
          totalValue: 0,
          totalStaked: 0,
          totalFarming: 0,
          totalEarned: 0,
          activePositions: 0,
          stakingPositions: [],
          farmingPositions: []
        };
      }

      const [stakingPositions, farmingPositions] = await Promise.all([
        this.getStakingPositions(walletAddress),
        this.getYieldFarmingPositions(walletAddress)
      ]);

      const totalStaked = stakingPositions?.reduce((sum, pos) => sum + pos?.currentValue, 0);
      const totalFarming = farmingPositions?.reduce((sum, pos) => sum + pos?.currentValue, 0);
      const totalEarned = [
        ...stakingPositions?.map(p => p?.earnedValue),
        ...farmingPositions?.map(p => p?.earned)
      ]?.reduce((sum, val) => sum + val, 0);

      return {
        totalValue: totalStaked + totalFarming,
        totalStaked,
        totalFarming,
        totalEarned,
        activePositions: stakingPositions?.length + farmingPositions?.length,
        stakingPositions,
        farmingPositions
      };
    } catch (error) {
      console.error('Error fetching DeFi portfolio summary:', error);
      return {
        totalValue: 0,
        totalStaked: 0,
        totalFarming: 0,
        totalEarned: 0,
        activePositions: 0,
        stakingPositions: [],
        farmingPositions: []
      };
    }
  }
}

export const defiService = new DeFiService();
export default defiService;
